import type { Plan } from "./schema.js";
import { RELAY_LEGS_PER_ATHLETE, RUN_DISTANCE_KM, STATIONS } from "./stations.js";
import type { StationId } from "./stations.js";

export type SegmentType = "run" | "roxzone" | "station" | "handover";

export interface Segment {
  /** Stable id, e.g. `run-1`, `roxzone-1`, `station-1`, `handover-1`. */
  id: string;
  type: SegmentType;
  /** Race leg (1–8) the segment belongs to. */
  leg: number;
  label: string;
  /** At most 8 characters, for watch faces. */
  shortLabel: string;
  stationId?: StationId;
  targetSec: number;
  /** Elapsed race time at the start of the segment. */
  startSec: number;
  /** Elapsed race time at the end of the segment. */
  endSec: number;
  /** Athletes active in this segment. */
  athleteIds: string[];
}

export interface AthleteLoad {
  athleteId: string;
  runSec: number;
  /** Station time attributed to this athlete; for doubles, weighted by their split. */
  stationSec: number;
}

export interface PlanEstimate {
  totalSec: number;
  runSec: number;
  stationSec: number;
  roxzoneSec: number;
  handoverSec: number;
  segments: Segment[];
  athletes: AthleteLoad[];
}

/** Athletes responsible for a race leg (0-based). */
export function athletesForLeg(plan: Plan, legIndex: number): string[] {
  if (plan.format === "relay") {
    const athlete = plan.athletes[Math.floor(legIndex / RELAY_LEGS_PER_ATHLETE)];
    return athlete ? [athlete.id] : [];
  }
  return plan.athletes.map((a) => a.id);
}

/** Share (0–1) of a station's work done by an athlete. */
function stationShare(plan: Plan, legIndex: number, athleteId: string): number {
  const assigned = athletesForLeg(plan, legIndex);
  if (!assigned.includes(athleteId)) return 0;
  const split = plan.stations[legIndex]?.split;
  if (split === undefined) return 1 / assigned.length;
  return (split.find((s) => s.athleteId === athleteId)?.pct ?? 0) / 100;
}

/** Computes the target timeline and totals for a plan. Assumes the plan is valid. */
export function estimatePlan(plan: Plan): PlanEstimate {
  const segments: Segment[] = [];
  let elapsed = 0;

  const push = (segment: Omit<Segment, "startSec" | "endSec">) => {
    segments.push({ ...segment, startSec: elapsed, endSec: elapsed + segment.targetSec });
    elapsed += segment.targetSec;
  };

  STATIONS.forEach((station, i) => {
    const leg = i + 1;
    const run = plan.runs[i];
    const target = plan.stations[i];
    if (run === undefined || target === undefined) {
      throw new Error(`Plan is missing targets for leg ${leg}`);
    }
    const athleteIds = athletesForLeg(plan, i);

    push({
      id: `run-${leg}`,
      type: "run",
      leg,
      label: `Run ${leg}`,
      shortLabel: `Run ${leg}`,
      targetSec: run.paceSecPerKm * RUN_DISTANCE_KM,
      athleteIds,
    });
    push({
      id: `roxzone-${leg}`,
      type: "roxzone",
      leg,
      label: `Roxzone ${leg}`,
      shortLabel: `Rox ${leg}`,
      targetSec: target.roxzoneSec,
      athleteIds,
    });
    push({
      id: `station-${leg}`,
      type: "station",
      leg,
      label: station.name,
      shortLabel: station.shortName,
      stationId: station.id,
      targetSec: target.targetSec,
      athleteIds,
    });

    const isRelayHandover =
      plan.format === "relay" && leg % RELAY_LEGS_PER_ATHLETE === 0 && leg < STATIONS.length;
    if (isRelayHandover) {
      push({
        id: `handover-${leg / RELAY_LEGS_PER_ATHLETE}`,
        type: "handover",
        leg,
        label: "Handover",
        shortLabel: "Handover",
        targetSec: plan.relayHandoverSec ?? 0,
        athleteIds: [...athleteIds, ...athletesForLeg(plan, i + 1)],
      });
    }
  });

  const sumOf = (type: SegmentType) =>
    segments.filter((s) => s.type === type).reduce((sum, s) => sum + s.targetSec, 0);

  const athletes = plan.athletes.map((athlete) => {
    let runSec = 0;
    let stationSec = 0;
    for (let i = 0; i < STATIONS.length; i++) {
      if (!athletesForLeg(plan, i).includes(athlete.id)) continue;
      runSec += plan.runs[i]!.paceSecPerKm * RUN_DISTANCE_KM;
      stationSec += plan.stations[i]!.targetSec * stationShare(plan, i, athlete.id);
    }
    return { athleteId: athlete.id, runSec, stationSec };
  });

  return {
    totalSec: elapsed,
    runSec: sumOf("run"),
    stationSec: sumOf("station"),
    roxzoneSec: sumOf("roxzone"),
    handoverSec: sumOf("handover"),
    segments,
    athletes,
  };
}
